import React, { useContext, useState } from "react";
import { FormControl, FormControlLabel, Grid, IconButton, Switch } from "@mui/material";
import Typography from "@mui/material/Typography";
import RemoveIcon from "@mui/icons-material/RemoveCircleOutline";
import AddIcon from "@mui/icons-material/AddCircleOutline";
import { DefinitelyAnArray } from "../../../sharedCode/HelperMethods";
import StringEditor from "../../../Components/inputs/stringEditor";
import DetailPage from "../../../Components/navigation/detailPage";
import { TicketProps } from "../FlightLegs/ticketFlightLegs";
import { TicketPax } from "../../../sharedCode/avroGraphql";
import { GlobalContext } from "../../../Utils/Contexts";
import DeleteConfirmationDialog from "../../../Components/DeleteConfirmationDialog";
import { addPassenger, saveTicketChanges } from "../../../Utils/TicketDatabase";
import { FIELD_LIMITS } from "../../../sharedCode/fieldLimits";

interface State {
    ShowDeleteConfirmation: boolean;
    ShowSeeManifestConfirmation: boolean;
    IdToDelete: number;
}

export function TicketPassengers(props: TicketProps): JSX.Element {
    const context = useContext(GlobalContext);

    const [localState, setLocalState] = useState<State>({
        ShowDeleteConfirmation: false,
        ShowSeeManifestConfirmation: false,
        IdToDelete: 0,
    });

    const paxList = DefinitelyAnArray(props.ticket.Pax);

    return (
        <DetailPage>
            <FormControl fullWidth>
                <Grid item container xs={12} alignItems={"center"} justifyContent={"left"} sx={{ marginBottom: 2 }}>
                    <Typography>LIST ON TICKET</Typography>
                    <FormControl sx={{ marginLeft: 1 }}>
                        <FormControlLabel
                            control={
                                <Switch checked={props.ticket.see_passenger_manifest === 1} color={"secondary"} onChange={handleSeeManifestChange} disabled={props.isReadOnly} />
                            }
                            label="SEE MANIFEST"
                        />
                    </FormControl>
                </Grid>

                {props.ticket.see_passenger_manifest !== 1 && (
                    <>
                        {localState.ShowDeleteConfirmation && (
                            <DeleteConfirmationDialog entityName={"PASSENGER"} onClose={handleDeleteCanceled} onConfirm={handleDeleteConfirmed} />
                        )}
                        {localState.ShowSeeManifestConfirmation && (
                            <DeleteConfirmationDialog entityName={"PASSENGER LIST"} onClose={handleSeeManifestCanceled} onConfirm={handleSeeManifestConfirmed} />
                        )}
                        <Typography>PASSENGER NAMES</Typography>
                        {paxList.map((passenger) => {
                            return (
                                <Grid key={`r${passenger.id}-${paxList.length}`} container flexDirection={"row"}>
                                    <Grid flex={1} item>
                                        <StringEditor
                                            labelText="PASSENGER NAME"
                                            initialValue={passenger.name}
                                            sourceId={passenger.id}
                                            sourceProperty={"name"}
                                            isDisabled={props.isReadOnly}
                                            isRequired={true}
                                            mergeChanges={mergePaxChanges}
                                            errors={props.errors}
                                            containerUrl={"ticket/pax"}
                                            maxLength={FIELD_LIMITS.pax_name}
                                        />
                                    </Grid>
                                    <Grid item>
                                        <IconButton
                                            disabled={props.isReadOnly}
                                            size="large"
                                            edge="start"
                                            sx={{ m: 2 }}
                                            onClick={() => {
                                                handleDelete(passenger);
                                            }}>
                                            <RemoveIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            );
                        })}
                        <Typography>
                            <IconButton disabled={props.isReadOnly} size="large" color="inherit" sx={{ ml: 2, mr: 2, alignItems: "left" }} onClick={handleAddClicked}>
                                <AddIcon />
                            </IconButton>
                        </Typography>
                    </>
                )}
            </FormControl>
        </DetailPage>
    );

    function handleAddClicked(): void {
        const id = props.ticket.nextLocalId;
        const newPassenger = { id, name: "", ticket_id: props.ticket.id } as TicketPax;

        addPassenger(props.ticket.id, newPassenger).catch(() => props.showForceReloadDialogue());
    }

    function handleDelete(paxToDelete: TicketPax): void {
        setLocalState((prev) => {
            return { ...prev, ShowDeleteConfirmation: true, IdToDelete: paxToDelete.id };
        });
    }

    function handleDeleteCanceled(): void {
        setLocalState((prev) => {
            return { ...prev, ShowDeleteConfirmation: false, IdToDelete: 0 };
        });
    }

    function handleDeleteConfirmed(): void {
        if (!props.ticket.Pax) return;
        const paxToDelete = props.ticket.Pax.find((p) => p?.id === localState.IdToDelete);
        if (paxToDelete) {
            const indexToDelete = props.ticket.Pax.indexOf(paxToDelete);
            if (indexToDelete > -1) {
                const newList = [...props.ticket.Pax.slice(0, indexToDelete), ...props.ticket.Pax.slice(indexToDelete + 1)];
                saveTicketChanges(props.ticket.id, { Pax: newList }).catch(() => props.showForceReloadDialogue());
            }
        }
    }

    // show a confirmation dialog if they will delete any passengers when turning on see manifest
    function handleSeeManifestChange(e: React.ChangeEvent<HTMLInputElement>) {
        if (!e.target.checked) {
            saveTicketChanges(props.ticket.id, { see_passenger_manifest: 0 }).catch(() => props.showForceReloadDialogue());
        } else {
            if (props.ticket.Pax?.length ?? -1 > 0) {
                setLocalState((prev) => {
                    return { ...prev, ShowSeeManifestConfirmation: true };
                });
            } else {
                saveTicketChanges(props.ticket.id, { see_passenger_manifest: 1 }).catch(() => props.showForceReloadDialogue());
            }
        }
    }

    function handleSeeManifestCanceled(): void {
        setLocalState((prev) => {
            return { ...prev, ShowSeeManifestConfirmation: false };
        });
    }

    async function handleSeeManifestConfirmed(): Promise<void> {
        await saveTicketChanges(props.ticket.id, { Pax: [], see_passenger_manifest: 1 }).catch(() => props.showForceReloadDialogue());
        setLocalState((prev) => {
            return { ...prev, ShowSeeManifestConfirmation: false };
        });
    }

    function mergePaxChanges(passengerId: number, changes: Record<string, unknown>) {
        const pax = props.ticket?.Pax?.map((passenger) => {
            if (passenger?.id === passengerId) {
                return { ...passenger, ...changes };
            } else {
                return passenger;
            }
        });

        saveTicketChanges(props.ticket.id, { Pax: pax }).catch(() => props.showForceReloadDialogue());
    }
}
